import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BlockMenu, BlockReferencedIndicator, EditableText } from '@/components/atoms';
import { useNodeName } from '@/context/names/names-context';
import type { RootState } from '@/context/store/store';
import {
  moveNode,
  type DocumentEntity,
} from '@/context/store/slices/documentDataSlice';
import {
  setAddBlockIntent,
  setNewBlockModal,
  setDeleteBlockConfirmationModal,
} from '@/context/store/slices/siteSlice';
import { styleRuleToLeafCssVars, useStyleRule } from '@/lib/style-profile';
import { useSiblingPosition } from '@/hooks/use-sibling-position';
import { useReportActiveBlock } from '@/hooks/use-report-active-block';

import './document-editor-text.scss';

type Props = {
  node: DocumentEntity & { type: 'text' };
  depth?: number;
};

const STYLE_PLACEHOLDERS: Record<string, string> = {
  body: '',
  note: 'Green text: …',
  example: 'Example: …',
  exception: 'Exception: …',
};

function DocumentEditorText({ node, depth = 0 }: Props) {
  const bookId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[0],
  );
  const nodeId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[1],
  );
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const name = useNodeName(node.id);
  const rule = useStyleRule(node.style, depth);
  const styleVars = styleRuleToLeafCssVars(rule);
  const position = useSiblingPosition(node.id);
  const reportActive = useReportActiveBlock(node);

  function handleMoveUp() {
    if (!position.canMoveUp) return;
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex - 1,
      }),
    );
  }

  function handleMoveDown() {
    if (!position.canMoveDown) return;
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex + 1,
      }),
    );
  }

  return (
    <div
      data-type="card"
      className={`doc-editor-text is-${node.style} ${isMenuOpen ? 'is-menu-open' : ''}`}
      style={styleVars}
      onFocusCapture={reportActive}
    >
      <div className="doc-editor-text__toolbar">
        <BlockMenu
          variant="leaf"
          showNumbering={false}
          onDelete={() => {
            dispatch(
              setDeleteBlockConfirmationModal({
                open: true,
                data: {
                  bookId: bookId ?? '',
                  nodeId: nodeId ?? '',
                  blockId: node.id,
                  blockType: node.style,
                },
              }),
            );
          }}
          onAddAbove={() => {
            dispatch(
              setAddBlockIntent({ mode: 'sibling-before', targetId: node.id }),
            );
            dispatch(setNewBlockModal(true));
          }}
          onAddBelow={() => {
            dispatch(
              setAddBlockIntent({ mode: 'sibling-after', targetId: node.id }),
            );
            dispatch(setNewBlockModal(true));
          }}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          canMoveUp={position.canMoveUp}
          canMoveDown={position.canMoveDown}
          onOpenChange={setIsMenuOpen}
        />
        <BlockReferencedIndicator blockId={node.id} variant="compact" />
      </div>

      {name && <h3 className="doc-editor-text__name">{name}</h3>}

      <EditableText
        nodeId={node.id}
        text={node.text}
        placeholder={STYLE_PLACEHOLDERS[node.style] ?? ''}
        className="doc-editor-text__content"
      />
    </div>
  );
}

export { DocumentEditorText };
